import React, { useImperativeHandle, forwardRef } from 'react';
import { format } from 'date-fns';
import ReportLayout from '../core/ReportLayout';
import { formatCurrency, formatNumber } from '../shared/utils';
import { exportToPDF, exportToExcel } from '../shared/exportUtils';
import { FileText } from 'lucide-react';
import { successToast, errorToast } from '../../../lib/helpers';

const DailySales = forwardRef(function DailySales({ data, isLoading, hideHeader = false }, ref) {
    const handleExportPDF = () => {
        try {
            if (!data?.daily || !data?.totals) {
                errorToast('No data available to export');
                return;
            }

            const tableData = data.daily.map(day => ({
                date: format(new Date(day.date), 'dd/MM/yy'),
                invoices: day.invoiceCount || 0,
                credits: day.creditCount || 0,
                total: day.totalCount || 0,
                invoiceValue: day.invoiceValue || 0,
                creditValue: Math.abs(day.creditValue || 0),
                goods: day.goods || 0,
                vat: day.vat || 0,
                totalWithVat: day.total || 0
            }));

            exportToPDF({
                title: 'Daily Sales Report',
                subtitle: 'Daily sales breakdown',
                filename: `daily-sales-${new Date().toISOString().split('T')[0]}.pdf`,
                columns: [
                    { key: 'date', label: 'Date', format: 'text' },
                    { key: 'invoices', label: 'Invoices', format: 'number' },
                    { key: 'credits', label: 'Credits', format: 'number' },
                    { key: 'total', label: 'Total', format: 'number' },
                    { key: 'invoiceValue', label: 'Invoice Value', format: 'currency' },
                    { key: 'creditValue', label: 'Credit Value', format: 'currency' },
                    { key: 'goods', label: 'Goods', format: 'currency' },
                    { key: 'vat', label: 'VAT', format: 'currency' },
                    { key: 'totalWithVat', label: 'Total with VAT', format: 'currency' }
                ],
                tableData
            });
            successToast('PDF exported successfully');
        } catch (error) {
            console.error('PDF export failed:', error);
            errorToast('Failed to export PDF');
        }
    };

    const handleExportExcel = () => {
        try {
            if (!data?.daily || !data?.totals) {
                errorToast('No data available to export');
                return;
            }

            const tableData = data.daily.map(day => ({
                date: format(new Date(day.date), 'dd/MM/yy'),
                invoices: day.invoiceCount || 0,
                credits: day.creditCount || 0,
                total: day.totalCount || 0,
                invoiceValue: day.invoiceValue || 0,
                creditValue: Math.abs(day.creditValue || 0),
                goods: day.goods || 0,
                vat: day.vat || 0,
                totalWithVat: day.total || 0
            }));

            exportToExcel(tableData, {
                title: 'Daily Sales Report',
                filename: `daily-sales-${new Date().toISOString().split('T')[0]}.xlsx`
            });
            successToast('Excel file exported successfully');
        } catch (error) {
            console.error('Excel export failed:', error);
            errorToast('Failed to export Excel file');
        }
    };

    const handleEmailReport = () => {
        errorToast('Email functionality coming soon');
    };

    // Expose export handlers via ref
    useImperativeHandle(ref, () => ({
        handleExportPDF,
        handleExportExcel,
        handleEmailReport
    }));

    if (isLoading) {
        if (hideHeader) {
            return (
                <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500">Loading sales data...</div>
                </div>
            );
        }
        return (
            <ReportLayout
                title="Daily Sales"
                subtitle="Daily sales breakdown"
                loading={isLoading}
                icon={FileText}
                iconColor="orange"
            >
                <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500">Loading sales data...</div>
                </div>
            </ReportLayout>
        );
    }

    if (!data?.daily || !data?.totals) {
        if (hideHeader) {
            return (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                    <p className="text-gray-500">No data available</p>
                </div>
            );
        }
        return (
            <ReportLayout
                title="Daily Sales"
                subtitle="Daily sales breakdown"
                icon={FileText}
                iconColor="orange"
                onExportPDF={handleExportPDF}
                onExportExcel={handleExportExcel}
                onEmailReport={handleEmailReport}
            >
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                    <p className="text-gray-500">No data available</p>
                </div>
            </ReportLayout>
        );
    }

    const { totals, daily } = data;

    const content = (
        <div className="space-y-4">
            {/* Overall KPI Cards - Only show when not using hideHeader (standalone mode) */}
            {!hideHeader && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-blue-500">
                        <div className="text-sm font-medium text-gray-600 mb-1">Total Sales</div>
                        <div className="text-2xl font-bold text-gray-900">{formatCurrency(totals.total || 0)}</div>
                        <div className="text-xs text-gray-500 mt-1">With VAT</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-green-500">
                        <div className="text-sm font-medium text-gray-600 mb-1">Total Transactions</div>
                        <div className="text-2xl font-bold text-gray-900">{formatNumber(totals.totalCount || 0)}</div>
                        <div className="text-xs text-gray-500 mt-1">
                            {formatNumber(totals.invoiceCount || 0)} invoices • {formatNumber(totals.creditCount || 0)} credits
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-purple-500">
                        <div className="text-sm font-medium text-gray-600 mb-1">Invoice Value</div>
                        <div className="text-2xl font-bold text-gray-900">{formatCurrency(totals.invoiceValue || 0)}</div>
                        <div className="text-xs text-gray-500 mt-1">Total invoiced</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-red-500">
                        <div className="text-sm font-medium text-gray-600 mb-1">Credit Value</div>
                        <div className="text-2xl font-bold text-gray-900">{formatCurrency(Math.abs(totals.creditValue || 0))}</div>
                        <div className="text-xs text-gray-500 mt-1">Total credited</div>
                    </div>
                </div>
            )}

            {/* Daily Breakdown Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-lg font-semibold text-gray-900">Daily Breakdown</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Date
                                </th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Invs/Crns
                                </th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Total
                                </th>
                                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Invoice Value
                                </th>
                                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Credit Value
                                </th>
                                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Goods
                                </th>
                                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    VAT
                                </th>
                                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Total
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {daily.map((day) => (
                                <tr key={day.date} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {format(new Date(day.date), 'dd/MM/yy')}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                        <span className="text-green-600 font-medium">{formatNumber(day.invoiceCount || 0)}</span>
                                        <span className="mx-1 text-gray-400">/</span>
                                        <span className="text-red-600 font-medium">{formatNumber(day.creditCount || 0)}</span>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 font-medium">
                                        {formatNumber(day.totalCount || 0)}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 font-medium">
                                        {formatCurrency(day.invoiceValue || 0)}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-red-600 font-medium">
                                        {formatCurrency(Math.abs(day.creditValue || 0))}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">
                                        {formatCurrency(day.goods || 0)}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">
                                        {formatCurrency(day.vat || 0)}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 font-bold">
                                        {formatCurrency(day.total || 0)}
                                    </td>
                                </tr>
                            ))}
                            {/* Totals Row */}
                            <tr className="bg-gray-50 font-bold">
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">Totals</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                    <span className="text-green-600">{formatNumber(totals.invoiceCount || 0)}</span>
                                    <span className="mx-1 text-gray-400">/</span>
                                    <span className="text-red-600">{formatNumber(totals.creditCount || 0)}</span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                    {formatNumber(totals.totalCount || 0)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                                    {formatCurrency(totals.invoiceValue || 0)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-red-600">
                                    {formatCurrency(Math.abs(totals.creditValue || 0))}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                                    {formatCurrency(totals.goods || 0)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                                    {formatCurrency(totals.vat || 0)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                                    {formatCurrency(totals.total || 0)}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    if (hideHeader) {
        return <div>{content}</div>;
    }

    return (
        <ReportLayout
            title="Daily Sales"
            subtitle="Daily sales breakdown"
            icon={FileText}
            iconColor="orange"
            onExportPDF={handleExportPDF}
            onExportExcel={handleExportExcel}
            onEmailReport={handleEmailReport}
        >
            {content}
        </ReportLayout>
    );
});

export default DailySales;
